import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Chip from './Chip'
import { getZodiacSign } from '../utils/zodiac'
import './ProfileModal.css'

function getZodiacFromProfile(profile) {
  if (profile.zodiac != null && profile.zodiacSymbol != null) {
    return `${profile.zodiac} ${profile.zodiacSymbol}`
  }
  if (profile.date_of_birth) {
    const d = new Date(profile.date_of_birth)
    const z = getZodiacSign(d.getDate(), d.getMonth() + 1)
    return `${z.name} ${z.symbol}`
  }
  return ''
}

/**
 * Режимы модалки по ТЗ:
 * - likes-you: лайк / дизлайк / суперлайк (с подпиской) / написать (с подпиской)
 * - matches | your-likes: только кнопка чата
 * - chat-match: только "✨ See compatibility details"
 * - chat-no-match: like / superlike / dislike (чат не нужен)
 */
const ProfileModal = ({
  profile,
  onClose,
  mode = 'matches',
  hasSubscription = false,
  isFemale = false,
  onLike,
  onDislike,
  onSuperLike,
  onChat,
  onSeeCompatibility
}) => {
  const { t } = useTranslation()
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)

  if (!profile) return null

  const photos = profile.photos || []
  const currentPhoto = photos[currentPhotoIndex] || null
  const zodiacDisplay = useMemo(() => getZodiacFromProfile(profile), [profile])

  const handlePrevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex((prev) => prev - 1)
    }
  }

  const handleNextPhoto = () => {
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex((prev) => prev + 1)
    }
  }

  const showLikeDislikeSuperLike = mode === 'likes-you' || mode === 'chat-no-match'
  const showChat = (mode === 'likes-you' && (hasSubscription || isFemale)) || mode === 'matches' || (mode === 'your-likes' && (hasSubscription || isFemale))
  const showSuperLike = showLikeDislikeSuperLike && (hasSubscription || isFemale)
  const hideAllActions = mode === 'chat-profile'

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal profile-modal-full" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="profile-modal-close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="profile-modal-scroll">
          <div className="profile-modal-photo-wrapper">
            <div className="profile-modal-photo-carousel">
              {photos.length > 1 && (
                <>
                  {currentPhotoIndex > 0 && (
                    <button
                      type="button"
                      className="profile-modal-photo-nav-btn profile-modal-photo-nav-btn-left"
                      onClick={handlePrevPhoto}
                      aria-label={t('common.previousPhoto')}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                  )}
                  {currentPhotoIndex < photos.length - 1 && (
                    <button
                      type="button"
                      className="profile-modal-photo-nav-btn profile-modal-photo-nav-btn-right"
                      onClick={handleNextPhoto}
                      aria-label={t('common.nextPhoto')}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}
                </>
              )}
              {currentPhoto ? (
                <img src={currentPhoto} alt={profile.name} />
              ) : (
                <div className="profile-modal-photo-placeholder">{profile.name?.[0] || '?'}</div>
              )}
              {photos.length > 1 && (
                <div className="profile-modal-photo-indicators">
                  {photos.map((_, index) => (
                    <span
                      key={index}
                      className={`profile-modal-photo-dot ${index === currentPhotoIndex ? 'active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="profile-modal-content">
            <div className="profile-modal-header">
              <h2 className="profile-modal-name">{profile.name}{profile.age != null ? `, ${profile.age}` : ''}</h2>
              {zodiacDisplay && <p className="profile-modal-zodiac">{zodiacDisplay}</p>}
            </div>

            {profile.interests && profile.interests.length > 0 && (
              <div className="profile-modal-interests">
                {profile.interests.slice(0, 6).map((interest, index) => {
                  const tr = t('onboarding.interestsItems.' + interest)
                  const label = tr === 'onboarding.interestsItems.' + interest ? interest : tr
                  return <Chip key={index} active={false}>{label}</Chip>
                })}
              </div>
            )}
            {onSeeCompatibility && (
              <button type="button" className="profile-modal-cta" onClick={() => onSeeCompatibility()}>
                {t('card.seeCompatibility')}
              </button>
            )}
          </div>
        </div>

        <div className="profile-modal-actions-fixed">
          {!hideAllActions && showLikeDislikeSuperLike && (
            <>
              <button type="button" className="profile-modal-action-btn profile-modal-dislike" onClick={() => onDislike?.()} aria-label="Dislike">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              {showSuperLike && (
                <button type="button" className="profile-modal-action-btn profile-modal-superlike" onClick={() => onSuperLike?.()} aria-label="Super like">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
              )}
              <button type="button" className="profile-modal-action-btn profile-modal-like" onClick={() => onLike?.()} aria-label="Like">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </>
          )}
          {!hideAllActions && showChat && (
            <button type="button" className="profile-modal-action-btn profile-modal-chat" onClick={() => onChat?.()} aria-label="Chat">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileModal
